/**
 * Shared layout for /trust/* routes.
 *
 * Per Round 2: /trust mounts at the top level of the SPA (not inside the
 * Win95 desktop). Signed-out visitors see public sub-routes (architecture,
 * data-residency); signed-in visitors get the full set including
 * permissions, audit, and sandbox.
 *
 * Memory: project_repancy_demo_in_convex.md, MILESTONE-A.md §A.4
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { testIds } from '../../lib/testIds';

interface NavLink {
  to: string;
  label: string;
  /** When true, only shown to signed-in visitors. */
  requiresAuth?: boolean;
}

const NAV: NavLink[] = [
  { to: '/trust', label: 'Overview' },
  { to: '/trust/architecture', label: 'Architecture' },
  { to: '/trust/data-residency', label: 'Data residency' },
  { to: '/trust/permissions', label: 'Your permissions', requiresAuth: true },
  { to: '/trust/audit', label: 'Audit log', requiresAuth: true },
  { to: '/trust/sandbox', label: 'Sandbox', requiresAuth: true },
];

export function TrustLayout({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const location = useLocation();
  return (
    <div
      data-testid={testIds.trust.root}
      className="min-h-screen bg-[rgb(236,232,227)] text-black"
    >
      <header className="flex items-center justify-between gap-4 bg-gradient-to-b from-[var(--xp-selection)] to-[#000040] px-6 py-3.5 text-white">
        <Link to="/" className="flex items-center gap-2.5 text-white no-underline">
          <span className="text-lg">🔒</span>
          <span className="text-base font-bold">Repancy — Trust</span>
        </Link>
        <Link to="/" className="text-xs text-white underline">
          ← Back to app
        </Link>
      </header>

      <div className="mx-auto flex max-w-[1100px]">
        <nav className="w-[220px] border-r border-black/10 px-4 py-6 min-h-[calc(100vh-56px)]">
          {NAV.filter((n) => !n.requiresAuth || isSignedIn).map((n) => {
            const active =
              n.to === '/trust'
                ? location.pathname === '/trust'
                : location.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={testIds.trust.navLink(
                  n.to.replace('/trust', '').replace('/', '') || 'overview',
                )}
                className={`block mb-1 rounded-sm px-3 py-2 text-[13px] no-underline ${
                  active ? 'bg-[var(--xp-selection)] text-white' : 'text-black hover:bg-black/5'
                }`}
              >
                {n.label}
              </Link>
            );
          })}
          {!isSignedIn && (
            <div className="mt-4 text-xs text-black italic">
              Sign in to view your real Entra permissions, audit log, and the live
              Microsoft Graph sandbox.
            </div>
          )}
        </nav>

        <main className="flex-1 px-8 py-6 text-black">{children}</main>
      </div>
    </div>
  );
}
